


import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Loader2 } from 'lucide-react';

const JoinGroup = () => {
    const navigate = useNavigate();
    const [groupCode, setGroupCode] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [groups, setGroups] = useState([]);

    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_URL}/api/fetchgroups`);
                setGroups(response.data || []);
            } catch (error) {
                console.error('Error fetching groups:', error);
                setError('Failed to fetch groups. Please try again later.');
            }
        };
        fetchGroups();
    }, []);

    const getCurrentUserId = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('Unauthorized');
        }

        const response = await fetch(`${process.env.REACT_APP_URL}/api/user/getuser/${token}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data.customer_generated_id;
    };

    const checkUserInGroups = (userId, groups) => {
        return groups.some(group =>
            group.groupmembers.some(member =>
                member.customer_generated_id === userId
            )
        );
    };

    const validateGroupCode = (code) => {
        return groups.some(group => group.groupcode === code.trim().toUpperCase());
    };

    const handleGroupCodeChange = (e) => {
        const code = e.target.value.toUpperCase();
        setGroupCode(code);
        setError('');

        if (code.length === 6 && !validateGroupCode(code)) {
            setError('Invalid group code. Please check and try again.');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmedCode = groupCode.trim().toUpperCase();

        if (!trimmedCode) {
            setError('Please enter a group code');
            return;
        }

        if (!validateGroupCode(trimmedCode)) {
            setError('Invalid group code. Please check and try again.');
            return;
        }

        setLoading(true);
        try {

            const usertoken = localStorage.getItem('token');
            if (!usertoken) {
                navigate(`/signin?groupCode=${trimmedCode}`);
                return;
            }
    
            const userId = await getCurrentUserId();

            if (checkUserInGroups(userId, groups)) {
                setError('You are already a member of a group.');
                setLoading(false);
                return;
            }

            const token = localStorage.getItem('token');
            await axios.put(
                `${process.env.REACT_APP_URL}/api/updategroup/${trimmedCode}`,
                {},
                { headers: { 'token': token } }
            );
            localStorage.setItem('groupCode', trimmedCode);

            navigate(`/groups/${trimmedCode}`);
        } catch (error) {
            console.error('Error joining group:', error);
            setError(error.response?.data?.message || 'Failed to join group. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white p-4 sm:p-8">
            <div className="max-w-md mx-auto">
                <h1 className="text-3xl font-bold text-center mb-8 text-orange-600">
                    Join Shopping Group
                </h1>

                <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg space-y-6">
                    <button
                        onClick={() => navigate('/shop')}
                        className="bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg text-gray-600 hover:text-gray-800 flex items-center gap-2 group transition-all"
                    >
                        <span className="group-hover:-translate-x-1 transition-transform">←</span>
                        Back to options
                    </button>

                    <div className="text-center space-y-4">
                        <div className="h-20 w-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
                            <ArrowRight className="h-10 w-10 text-orange-500" />
                        </div>
                        <h2 className="text-2xl font-semibold text-gray-800">
                            Enter Group Code
                        </h2>
                        <p className="text-gray-600">
                            Enter the 6-digit code shared with you to join the shopping group
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={groupCode}
                                    onChange={handleGroupCodeChange}
                                    maxLength={6}
                                    className={`w-full p-4 text-center text-2xl tracking-widest font-mono border-2 
                                        ${error ? 'border-red-500' : 'border-gray-200'} 
                                        rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 
                                        transition-colors uppercase`}
                                    placeholder="XXXXXX"
                                />
                                {error && (
                                    <p className="absolute -bottom-6 left-0 w-full text-center text-sm text-red-500">
                                        {error}
                                    </p>
                                )}
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || !groupCode.trim() || error}
                            className="w-full p-4 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 
                                text-white rounded-lg font-medium transition-all hover:shadow-md 
                                disabled:opacity-50 disabled:cursor-not-allowed
                                flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Joining...
                                </>
                            ) : (
                                'Join Group'
                            )}
                        </button>
                    </form>

                    <div className="pt-4 text-center">
                        <p className="text-sm text-gray-600">
                            Don't have a group code?{' '}
                            <button
                                onClick={() => navigate('/creategroup')}
                                className="text-orange-500 hover:text-orange-600 font-medium"
                            >
                                Create a new group
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default JoinGroup;
